// utils/PushNotificationService.js - Fixed version with proper service worker handling
import { getMessaging, getToken, deleteToken, onMessage, isSupported as isMessagingSupported } from 'firebase/messaging';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

// Build the SW URL with Firebase config in the query string so the SW can init Firebase.
// Must match the URL used in registerServiceWorkers (src/App.js) to avoid re-registration churn.
export const getMessagingSwUrl = () => '/firebase-messaging-sw.js?' + new URLSearchParams({
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY || '',
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || '',
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || '',
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || '',
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || '',
    appId: process.env.REACT_APP_FIREBASE_APP_ID || ''
}).toString();

class PushNotificationService {
    constructor() {
        this.messaging = null;
        this.isSupported = false;
        this.serviceWorkerRegistration = null;
        this.init();
    }

    async init() {
        try {
            if (typeof window === 'undefined') return;
            // Check if push notifications are supported
            this.isSupported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

            if (this.isSupported) {
                // Firebase-level support check (e.g. IndexedDB availability)
                const fcmSupported = await isMessagingSupported().catch(() => false);
                if (!fcmSupported) {
                    this.isSupported = false;
                    return;
                }
                // Wait for service worker to be ready before initializing messaging
                await this.ensureServiceWorkerReady();
                if (!this.messaging) {
                    this.messaging = getMessaging();
                    this.setupMessageListener();
                }
            }
        } catch (error) {
            //console.error('Push notification initialization failed:', error);
        }
    }

    async ensureServiceWorkerReady() {
        if (!('serviceWorker' in navigator)) {
            throw new Error('Service Worker not supported');
        }

        try {
            // Use the root-scope registration created in App.js (registerServiceWorkers)
            let registration = await navigator.serviceWorker.getRegistration('/');

            if (!registration) {
                registration = await navigator.serviceWorker.register(getMessagingSwUrl(), {
                    scope: '/'
                });
            }

            // Wait for the service worker to be ready
            if (registration.installing) {
                const installing = registration.installing;
                await new Promise((resolve) => {
                    installing.addEventListener('statechange', (e) => {
                        if (e.target.state === 'installed' || e.target.state === 'activated' || e.target.state === 'redundant') {
                            resolve();
                        }
                    });
                });
            }

            // Ensure we have an active service worker
            const ready = await navigator.serviceWorker.ready;
            this.serviceWorkerRegistration = ready || registration;

            return this.serviceWorkerRegistration;
        } catch (error) {
            throw error;
        }
    }

    async requestPermission() {
        if (!this.isSupported) {
            throw new Error('Push notifications not supported');
        }

        // Ensure service worker is ready before requesting permission
        await this.ensureServiceWorkerReady();

        const permission = await Notification.requestPermission();

        if (permission === 'granted') {
            return await this.getRegistrationToken();
        }
        throw new Error('Notification permission denied');
    }

    async getRegistrationToken() {
        if (!this.messaging) {
            // Re-initialize messaging if needed
            await this.init();
        }

        if (!this.messaging) {
            throw new Error('Firebase messaging not initialized');
        }

        const fetchToken = async () => {
            const registration = (await navigator.serviceWorker.getRegistration('/')) || await this.ensureServiceWorkerReady();
            this.serviceWorkerRegistration = registration;
            return getToken(this.messaging, {
                vapidKey: process.env.REACT_APP_VAPID_KEY,
                serviceWorkerRegistration: registration
            });
        };

        try {
            const token = await fetchToken();
            if (token) return token;
            throw new Error('No registration token available');
        } catch (error) {
            // If it's a service worker error, re-ensure the SW and retry once
            const msg = error?.message || '';
            if (msg.includes('Service Worker') || msg.includes('no active Service Worker')) {
                await this.ensureServiceWorkerReady();
                const token = await fetchToken();
                if (token) return token;
                throw new Error('No registration token available');
            }
            throw error;
        }
    }

    // Save the FCM token server-side (and optionally subscribe to an event's spot alerts)
    async saveToken(fcmToken, eventId) {
        if (!fcmToken) throw new Error('FCM token is required');
        const saveFCMToken = httpsCallable(functions, 'saveFCMToken');
        await saveFCMToken({ token: fcmToken, eventId: eventId || undefined });
    }

    setupMessageListener() {
        if (!this.messaging) return;

        onMessage(this.messaging, (payload) => {
            // Show notification when app is in foreground
            this.showNotification(payload);
        });
    }

    showNotification(payload) {
        if (!this.serviceWorkerRegistration || !payload) {
            return;
        }

        const notif = payload.notification || payload.data || {};
        const data = payload.data || {};
        const url = data.url || data.click_action || payload.fcmOptions?.link ||
            (data.eventId ? `/event/${data.eventId}` : '/');

        this.serviceWorkerRegistration.showNotification(notif.title || 'Notification', {
            body: notif.body || '',
            icon: '/logo192.png',
            badge: '/logo192.png',
            tag: data.tag || (data.eventId ? `event-${data.eventId}` : 'general'),
            data: { url }
        });
    }

    // userId is kept for API compatibility; the callable uses the authenticated uid.
    async subscribeToEventNotifications(userId, eventId, fcmToken) {
        await this.saveToken(fcmToken, eventId);
    }

    async unsubscribeFromEventNotifications(userId, eventId) {
        if (!eventId) return;
        const removeFCMToken = httpsCallable(functions, 'removeFCMToken');
        // Only removes the event subscription; the device token stays registered
        await removeFCMToken({ eventId });
    }

    // Remove this device's token from the user's profile and invalidate it (call on logout).
    // Never throws.
    async removeTokenOnLogout() {
        try {
            if (typeof window === 'undefined' || !('Notification' in window)) return;
            if (Notification.permission !== 'granted') return;
            if (!this.isSupported) return;

            if (!this.messaging) {
                await this.init();
            }
            if (!this.messaging) return;

            let token = null;
            try {
                const registration = await navigator.serviceWorker.getRegistration('/');
                if (!registration) return;
                token = await getToken(this.messaging, {
                    vapidKey: process.env.REACT_APP_VAPID_KEY,
                    serviceWorkerRegistration: registration
                });
            } catch (e) {
                token = null;
            }

            if (token) {
                try {
                    const removeFCMToken = httpsCallable(functions, 'removeFCMToken');
                    await removeFCMToken({ token });
                } catch (e) {
                    // ignore - still try to delete the local token
                }
            }

            try {
                await deleteToken(this.messaging);
            } catch (e) {
                // ignore
            }
        } catch (error) {
            // never throw from logout cleanup
        }
    }

    // Check if service worker is properly registered and active
    async checkServiceWorkerStatus() {
        if (!('serviceWorker' in navigator)) {
            return { supported: false, reason: 'Service Worker not supported' };
        }

        try {
            const registration = await navigator.serviceWorker.getRegistration('/');

            if (!registration) {
                return { supported: false, reason: 'Service Worker not registered' };
            }

            if (!registration.active) {
                return { supported: false, reason: 'Service Worker not active' };
            }

            return { supported: true, registration };
        } catch (error) {
            return { supported: false, reason: `Service Worker error: ${error.message}` };
        }
    }
}

const pushNotificationService = new PushNotificationService();

// Call from logout BEFORE signing out (callable needs auth). Never throws.
export async function removePushTokenOnLogout() {
    return pushNotificationService.removeTokenOnLogout();
}

export default pushNotificationService;
